import React from 'react';

interface TicketItem {
  nombre: string;
  presentacion?: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
}

interface TicketThermalProps {
  id?: string;
  numeroPedido: string;
  fecha: string;
  vendedor?: string;
  cliente?: string;
  items: TicketItem[];
  subtotal: number;
  iva: number;
  total: number;
  metodoPago: string;
  efectivoRecibido?: number;
  cambio?: number;
  preview?: boolean;
}

export const TicketThermal: React.FC<TicketThermalProps> = ({
  id = 'ticket-thermal',
  numeroPedido,
  fecha,
  vendedor,
  cliente,
  items,
  subtotal,
  iva,
  total,
  metodoPago,
  efectivoRecibido,
  cambio,
  preview = false,
}) => {
  const formatMoney = (amount: number) => `$${amount.toLocaleString('es-CO')}`;

  return (
    <div id={id} className={preview ? '' : 'hidden-print'}>
      <div className="thermal-ticket">
        {/* Encabezado */}
        <div className="ticket-header">
          <h1 className="ticket-title">CONFIFARMA AMIGA</h1>
          <p className="ticket-subtitle">Tu salud con confianza</p>
          <p className="ticket-info">NIT: 1152707214-8</p>
          <p className="ticket-info">Tel: 3193650538</p>
          <p className="ticket-info">Cra. 104A #103A - 24 B/Velez Apartadó Ant</p>
        </div>

        <div className="ticket-divider">================================</div>

        {/* Info del pedido */}
        <div className="ticket-section">
          <p className="ticket-row">
            <span className="ticket-label">FACTURA:</span>
            <span className="ticket-value">{numeroPedido}</span>
          </p>
          <p className="ticket-row">
            <span className="ticket-label">FECHA:</span>
            <span className="ticket-value">{fecha}</span>
          </p>
          <p className="ticket-row">
            <span className="ticket-label">VENDEDOR:</span>
            <span className="ticket-value">{vendedor || 'N/A'}</span>
          </p>
          {cliente && (
            <p className="ticket-row">
              <span className="ticket-label">CLIENTE:</span>
              <span className="ticket-value">{cliente}</span>
            </p>
          )}
          <p className="ticket-row">
            <span className="ticket-label">ARTICULOS:</span>
            <span className="ticket-value">{items.reduce((acc, i) => acc + i.cantidad, 0)}</span>
          </p>
        </div>

        <div className="ticket-divider">================================</div>

        {/* Items */}
        <div className="ticket-items">
          <div className="ticket-item-header">
            <span className="ticket-col-desc">DESCRIPCION</span>
            <span className="ticket-col-total">TOTAL</span>
          </div>
          {items.map((item, idx) => (
            <div key={idx} className="ticket-item">
              <div className="ticket-item-name">
                {item.nombre}
                {item.presentacion && item.presentacion !== 'unidad' && (
                  <span className="ticket-item-pres"> ({item.presentacion})</span>
                )}
              </div>
              <div className="ticket-item-row">
                <span className="ticket-col-qty">{item.cantidad} x {formatMoney(item.precioUnitario)}</span>
                <span className="ticket-col-total">{formatMoney(item.subtotal)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="ticket-divider">================================</div>

        {/* Totales */}
        <div className="ticket-totals">
          <div className="ticket-row">
            <span className="ticket-label">SUBTOTAL:</span>
            <span className="ticket-value">{formatMoney(subtotal)}</span>
          </div>
          {iva > 0 && (
            <div className="ticket-row">
              <span className="ticket-label">IVA (19%):</span>
              <span className="ticket-value">{formatMoney(iva)}</span>
            </div>
          )}
          <div className="ticket-row ticket-total-row">
            <span className="ticket-label">TOTAL A PAGAR:</span>
            <span className="ticket-value">{formatMoney(total)}</span>
          </div>
        </div>

        <div className="ticket-divider">================================</div>

        {/* Pago */}
        <div className="ticket-section">
          <div className="ticket-row">
            <span className="ticket-label">METODO PAGO:</span>
            <span className="ticket-value">{metodoPago}</span>
          </div>
          {metodoPago === 'Efectivo' && typeof efectivoRecibido === 'number' && efectivoRecibido > 0 && (
            <div className="ticket-row">
              <span className="ticket-label">EFECTIVO:</span>
              <span className="ticket-value">{formatMoney(efectivoRecibido)}</span>
            </div>
          )}
          {typeof cambio === 'number' && cambio > 0 && (
            <div className="ticket-row">
              <span className="ticket-label">CAMBIO:</span>
              <span className="ticket-value">{formatMoney(cambio)}</span>
            </div>
          )}
        </div>

        <div className="ticket-divider">================================</div>

        {/* Pie */}
        <div className="ticket-footer">
          <p>*** GRACIAS POR SU COMPRA ***</p>
          <p>ConfiFarma Amiga</p>
          <p className="ticket-small">Documento equivalente a factura de venta</p>
          <p className="ticket-small">No válido para reclamaciones de garantía</p>
        </div>
      </div>
    </div>
  );
};
